import { useEffect, useImperativeHandle } from "react";
import { EditorContent, useEditor, type Editor as TiptapEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extensions";
import { Bold, Italic, Underline, List, ListOrdered, Quote, Link2, Undo2, Redo2, RemoveFormatting } from "lucide-react";
import { IconButton } from "@dfws/ui";
import s from "./Compose.module.css";

export interface EditorHandle {
  focus(): void;
  insertHtml(html: string): void;
}

/** Rich text body. Formatting stays to what every mail client renders: bold, italic, underline, lists, quotes, links. */
export function Editor({ ref, html, placeholder, onChange, onSubmit, autoFocus, label }: {
  ref?: React.Ref<EditorHandle>;
  html: string;
  placeholder: string;
  onChange: (html: string) => void;
  onSubmit: () => void;
  autoFocus?: boolean;
  label: string;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false, horizontalRule: false, link: { openOnClick: false, autolink: true, protocols: ["https", "http", "mailto"], HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } } }),
      Placeholder.configure({ placeholder }),
    ],
    content: html,
    autofocus: autoFocus ? "start" : false,
    editorProps: {
      attributes: { class: s.prose ?? "", "aria-label": label, role: "textbox", "aria-multiline": "true" },
      handleKeyDown: (_view, e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
          e.preventDefault();
          onSubmit();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? "" : ed.getHTML()),
    immediatelyRender: true,
  });

  useImperativeHandle(ref, () => ({
    focus: () => editor?.commands.focus(),
    insertHtml: (h: string) => editor?.chain().focus().insertContent(h).run(),
  }));

  // An external change (an Undo that reopened the draft) replaces the content; typing never comes back here.
  useEffect(() => {
    if (editor && !editor.isFocused && html !== editor.getHTML() && !(html === "" && editor.isEmpty)) editor.commands.setContent(html, { emitUpdate: false });
  }, [html, editor]);

  return (
    <div className={s.editorWrap}>
      <EditorContent editor={editor} className={s.editor} />
      {editor && <Toolbar editor={editor} />}
    </div>
  );
}

function Toolbar({ editor }: { editor: TiptapEditor }) {
  const link = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link address", prev ?? "https://");
    if (url === null) return;
    if (url === "") editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else if (/^(https?:|mailto:)/i.test(url)) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const btn = (label: string, icon: React.ReactNode, run: () => void, active = false, shortcut?: string) => (
    <IconButton label={label} shortcut={shortcut} icon={icon} size="sm" pressed={active} onMouseDown={(e) => e.preventDefault()} onClick={run} />
  );
  return (
    <div className={s.toolbar} role="toolbar" aria-label="Formatting">
      {btn("Bold", <Bold />, () => editor.chain().focus().toggleBold().run(), editor.isActive("bold"), "Ctrl+B")}
      {btn("Italic", <Italic />, () => editor.chain().focus().toggleItalic().run(), editor.isActive("italic"), "Ctrl+I")}
      {btn("Underline", <Underline />, () => editor.chain().focus().toggleUnderline().run(), editor.isActive("underline"), "Ctrl+U")}
      <span className={s.sep} />
      {btn("Bulleted list", <List />, () => editor.chain().focus().toggleBulletList().run(), editor.isActive("bulletList"))}
      {btn("Numbered list", <ListOrdered />, () => editor.chain().focus().toggleOrderedList().run(), editor.isActive("orderedList"))}
      {btn("Quote", <Quote />, () => editor.chain().focus().toggleBlockquote().run(), editor.isActive("blockquote"))}
      {btn("Link", <Link2 />, link, editor.isActive("link"), "Ctrl+K")}
      {btn("Clear formatting", <RemoveFormatting />, () => editor.chain().focus().unsetAllMarks().clearNodes().run())}
      <span className={s.sep} />
      {btn("Undo typing", <Undo2 />, () => editor.chain().focus().undo().run(), false, "Ctrl+Z")}
      {btn("Redo typing", <Redo2 />, () => editor.chain().focus().redo().run(), false, "Ctrl+Y")}
    </div>
  );
}
